// Left-edge pull → open the app's rail sheet; left-swipe on the sheet (or
// scrim tap, handled by the sheet component) → close. The PHONE gesture
// vocabulary's first verb (2026-08-02 per Nika "off canvas menus, swipes,
// gestures … efficient, productive and cool"): every app teaches the same
// motion, so the hand learns it once — tickets filters, calendar controls,
// chat conversations, documents browse all answer to the same pull.
//
// Design constraints (deliberate):
// • The OPEN gesture arms only within EDGE_ZONE px of the viewport's left
//   edge — it can never fight panzoom boards, horizontal scrollers or the
//   tab strip, all of which start further in.
// • The sheet follows the finger (interruptible, transform-only writes on
//   the sheet node), commits past 35% width or a quick flick, and settles
//   with a WAAPI ease that collapses to instant under prefers-reduced-motion.
// • Coarse pointers only — desktop keeps its persistent rails.
//
// Wiring (an app's stage root, beside its other arm calls):
//   onRender: (el, s) => el.call('armEdgeSwipe', { stateKey: '_ticketsRailOpen' })
// The sheet component itself reads/writes the same root state key — see
// shared/components/MobileRailSheet.js.

export function armEdgeSwipe(opts) {
  // FA204 / frank trap — inlined as self-contained locals (single
  // `this`-bound export, pure constants, not cross-call state — see
  // longPress.js for the identical shape).
  const EDGE_ZONE = 24
  const COMMIT_FRACTION = 0.35
  const FLICK_PX_PER_MS = 0.35

  const el = this
  const node = el && el.node
  if (!node || node.__edgeSwipeArmed) return
  if (typeof el.call === 'function' && !el.call('isCoarsePointer')) return
  const stateKey = opts && opts.stateKey
  if (!stateKey) return
  node.__edgeSwipeArmed = true

  // Transient gesture state — closure-local, never reactive (mirrors the
  // armLongPress idiom; a mid-gesture x coordinate has no business in
  // signals).
  let tracking = false
  let startX = 0
  let startY = 0
  let startTs = 0
  let lastX = 0
  let axisLock = null // 'x' once horizontal intent is clear, 'veto' if vertical

  const root = () => (typeof el.getRootState === 'function' ? el.getRootState() : el.state?.root)
  const sheetNode = () =>
    node.ownerDocument.querySelector(`[data-mobile-rail="${stateKey}"] [data-rail-card]`)

  const setDrag = (px) => {
    const sheet = sheetNode()
    if (!sheet) return
    // Follow the finger from the closed position (-105%) toward 0.
    const w = sheet.getBoundingClientRect().width || 300
    const progress = Math.max(0, Math.min(1, px / w))
    sheet.style.transition = 'none'
    sheet.style.transform = `translateX(${(-1.05 + 1.05 * progress) * 100}%)`
  }

  const settle = (open) => {
    const sheet = sheetNode()
    if (sheet) {
      sheet.style.transition = ''
      sheet.style.transform = ''
    }
    const r = root()
    if (r && !!r[stateKey] !== open) r.update({ [stateKey]: open }, { preventFetch: true })
  }

  const onDown = (e) => {
    if (e.pointerType === 'mouse') return
    const r = root()
    if (r && r[stateKey]) return // open-state gestures belong to the sheet
    if (e.clientX > EDGE_ZONE) return
    tracking = true
    axisLock = null
    startX = lastX = e.clientX
    startY = e.clientY
    startTs = e.timeStamp
  }

  const onMove = (e) => {
    if (!tracking) return
    const dx = e.clientX - startX
    const dy = e.clientY - startY
    if (!axisLock) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return
      axisLock = Math.abs(dx) > Math.abs(dy) ? 'x' : 'veto'
    }
    if (axisLock === 'veto') {
      tracking = false
      return
    }
    lastX = e.clientX
    setDrag(dx)
  }

  const onUp = (e) => {
    if (!tracking) return
    tracking = false
    if (axisLock !== 'x') return
    const sheet = sheetNode()
    const w = (sheet && sheet.getBoundingClientRect().width) || 300
    const dx = lastX - startX
    const dt = Math.max(1, e.timeStamp - startTs)
    const commit = dx > w * COMMIT_FRACTION || dx / dt > FLICK_PX_PER_MS
    settle(commit)
  }

  // Passive — the drag writes transforms, never preventDefault; vertical
  // scrolling stays native (the axis veto drops the gesture instead).
  node.addEventListener('pointerdown', onDown, { passive: true })
  node.addEventListener('pointermove', onMove, { passive: true })
  node.addEventListener('pointerup', onUp, { passive: true })
  node.addEventListener('pointercancel', onUp, { passive: true })
}
